import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { api } from '../../services/api'
import { validatePassword, passwordsMatch } from '../../utils/validation'
import { ValidatedInput } from '../../components/ValidatedInput'
import { PasswordStrength } from '../../components/PasswordStrength'
import { AuthContainer, AuthCard, Form, ButtonContainer, Divider, BackText, StyledLink } from './styles'

export function ResetPassword() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [newPassword, setNewPassword] = useState('')
  const [confirmNewPassword, setConfirmNewPassword] = useState('')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  if (!token) {
    return (
      <AuthContainer>
        <AuthCard>
          <h1>Token Inválido</h1>
          <p>O link de recuperação de senha é inválido ou expirou.</p>
          <Divider />
          <BackText>
            <StyledLink to="/forgot-password">Solicitar novo link</StyledLink>
          </BackText>
        </AuthCard>
      </AuthContainer>
    )
  }

  const validateField = (name, value) => {
    let error = ''

    if (name === 'newPassword') {
      const validation = validatePassword(value)
      if (!value) {
        error = 'Senha é obrigatória'
      } else if (!validation.isValid) {
        error = 'A senha não atende aos requisitos'
      }

      if (confirmNewPassword && !passwordsMatch(value, confirmNewPassword)) {
        setErrors((prev) => ({ ...prev, confirmNewPassword: 'As senhas não coincidem' }))
      } else if (confirmNewPassword) {
        setErrors((prev) => ({ ...prev, confirmNewPassword: '' }))
      }
    }

    if (name === 'confirmNewPassword') {
      if (!value) {
        error = 'Confirmação de senha é obrigatória'
      } else if (!passwordsMatch(newPassword, value)) {
        error = 'As senhas não coincidem'
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }))
    return !error
  }

  const onBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    if (name === 'newPassword') validateField(name, newPassword)
    if (name === 'confirmNewPassword') validateField(name, confirmNewPassword)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    setTouched({ newPassword: true, confirmNewPassword: true })
    const passwordValid = validateField('newPassword', newPassword)
    const confirmPasswordValid = validateField('confirmNewPassword', confirmNewPassword)

    if (!passwordValid || !confirmPasswordValid) {
      return
    }

    setIsLoading(true)

    try {
      await api.post('/auth/password-reset/confirm', { token, newPassword })
      toast.success('Senha redefinida com sucesso!')
      navigate('/login')
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Token inválido ou expirado. Solicite um novo link.')
      } else {
        toast.error('Erro ao redefinir senha. Tente novamente.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const isFormValid =
    !errors.newPassword &&
    !errors.confirmNewPassword &&
    newPassword &&
    confirmNewPassword &&
    validatePassword(newPassword).isValid &&
    passwordsMatch(newPassword, confirmNewPassword)

  return (
    <AuthContainer>
      <AuthCard>
        <h1>Nova Senha</h1>
        <p>Digite sua nova senha</p>

        <Form onSubmit={handleSubmit}>
          <div>
            <ValidatedInput
              name="newPassword"
              type="password"
              label="Nova Senha"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
                if (touched.newPassword) validateField('newPassword', e.target.value)
              }}
              onBlur={() => onBlur('newPassword')}
              errors={errors}
              touched={touched}
              placeholder="Digite sua nova senha"
              autoComplete="new-password"
            />
            {newPassword && <PasswordStrength password={newPassword} />}
          </div>

          <ValidatedInput
            name="confirmNewPassword"
            type="password"
            label="Confirmar Nova Senha"
            value={confirmNewPassword}
            onChange={(e) => {
              setConfirmNewPassword(e.target.value)
              if (touched.confirmNewPassword) validateField('confirmNewPassword', e.target.value)
            }}
            onBlur={() => onBlur('confirmNewPassword')}
            errors={errors}
            touched={touched}
            placeholder="Digite a senha novamente"
            autoComplete="new-password"
          />

          <ButtonContainer>
            <button type="submit" disabled={!isFormValid || isLoading}>
              {isLoading ? 'Redefinindo...' : 'Redefinir Senha'}
            </button>
          </ButtonContainer>
        </Form>

        <Divider />

        <BackText>
          <StyledLink to="/login">Voltar para Login</StyledLink>
        </BackText>
      </AuthCard>
    </AuthContainer>
  )
}
